/* ── ManualReview ──────────────────────────────────────────────────
   PDF Manual Review — uploads a product installation manual PDF,
   identifies the product via Claude, sweeps the compliance corpus,
   and grades each requirement (PASS / PARTIAL / FAIL / N/A).

   Phases:
     idle               → drag & drop upload zone
     uploading          → sending file to server
     identifying        → Claude reading PDF, identifying product
     confirmation_needed→ user confirms ambiguous product type
     sweeping           → corpus search in progress
     grading            → grading requirements against manual
     complete           → summary bar + filterable gap list
     error              → error message + retry

   Export: PDF report (window.print) + copy all + per-gap clipboard
   Styling: Otto design tokens (otto-tokens.css)
   ────────────────────────────────────────────────────────────────── */

import { useCallback, useDeferredValue, useMemo, useRef, useState } from 'react';
import type {
  ManualReviewState,
  ReviewGap,
  ReviewGrade,
} from '../types';
import {
  REVIEW_GRADE_COLORS,
  REVIEW_GRADE_LABELS,
} from '../types';
import { streamManualReview } from '../api';

// ── Constants ──────────────────────────────────────────────────────

const DOMAIN_LABELS: Record<string, string> = {
  'ncc-compliance':        'NCC',
  'plumbing-installation': 'Plumbing',
  'watermark-scheme':      'WaterMark',
  'electrical-compliance': 'Electrical',
  'eess-platform':         'EESS',
  'acl-consumer-law':      'Consumer Law',
  'dda-compliance':        'DDA / Access',
  'structural-loads':      'Structural',
  'nz-building-code':      'NZ Building Code',
  'uk-eu-compliance':      'UK / EU',
  'jas-anz-accreditation': 'JAS-ANZ',
  'nata-accreditation':    'NATA',
};

const ALL_GRADES: ReviewGrade[] = ['PASS', 'PARTIAL', 'FAIL', 'NA'];

const PHASE_MESSAGES: Partial<Record<ManualReviewState['phase'], string>> = {
  uploading:   'Uploading PDF…',
  identifying: 'Identifying product from manual…',
  sweeping:    'Searching compliance corpus for applicable requirements…',
  grading:     'Grading requirements against manual content…',
};

const INITIAL_STATE: ManualReviewState = {
  phase: 'idle',
  fileName: '',
  productName: '',
  manufacturer: null,
  model: null,
  domains: [],
  gaps: [],
  total: 0,
  pass: 0,
  partial: 0,
  fail: 0,
  na: 0,
  complianceScore: 0,
  guesses: [],
  confirmedProductType: '',
  error: null,
};

// ── Props ──────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
}

// ── Component ──────────────────────────────────────────────────────

export default function ManualReview({ onClose }: Props) {
  const [state, setState] = useState<ManualReviewState>(INITIAL_STATE);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [gradeFilter, setGradeFilter] = useState<ReviewGrade | 'ALL'>('ALL');
  const [domainFilter, setDomainFilter] = useState<string>('ALL');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const deferredGaps = useDeferredValue(state.gaps);

  /* ── Helpers ──────────────────────────────────────────────────── */

  const update = useCallback((patch: Partial<ManualReviewState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    onClose();
  }, [onClose]);

  const handleReset = useCallback(() => {
    abortRef.current?.abort();
    setSelectedFile(null);
    setGradeFilter('ALL');
    setDomainFilter('ALL');
    setState(INITIAL_STATE);
  }, []);

  /* ── File selection ───────────────────────────────────────────── */

  const handleFileAccepted = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      update({ error: 'Only PDF files are accepted.', phase: 'error' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      update({ error: 'PDF is too large. Maximum size is 10MB.', phase: 'error' });
      return;
    }
    setSelectedFile(file);
    update({ fileName: file.name, error: null });
  }, [update]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileAccepted(file);
  }, [handleFileAccepted]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileAccepted(file);
    e.target.value = '';
  }, [handleFileAccepted]);

  /* ── Run review ───────────────────────────────────────────────── */

  const runReview = useCallback(async (
    file: File,
    confirmedType?: string,
    confirmedDomains?: string[],
  ) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    update({
      phase: 'uploading',
      gaps: [],
      error: null,
      productName: '',
      manufacturer: null,
      model: null,
      domains: [],
      total: 0,
      pass: 0,
      partial: 0,
      fail: 0,
      na: 0,
      complianceScore: 0,
      guesses: [],
    });

    await streamManualReview(
      file,
      {
        onStatus: (phase, data) => {
          const phaseMap: Record<string, ManualReviewState['phase']> = {
            identifying: 'identifying',
            identified:  'sweeping',
            sweeping:    'sweeping',
            grading:     'grading',
          };
          const mappedPhase = phaseMap[phase];
          if (mappedPhase) {
            update({
              phase: mappedPhase,
              ...(data.product_name ? { productName: data.product_name as string } : {}),
              ...(data.manufacturer ? { manufacturer: data.manufacturer as string } : {}),
              ...(data.model ? { model: data.model as string } : {}),
              ...(Array.isArray(data.domains) ? { domains: data.domains as string[] } : {}),
            });
          }
        },
        onGap: (gap) => {
          setState(prev => ({ ...prev, gaps: [...prev.gaps, gap] }));
        },
        onDone: (data) => {
          setState(prev => ({
            ...prev,
            phase: 'complete',
            total:           (data.total as number)  ?? prev.gaps.length,
            pass:            (data.pass as number)   ?? 0,
            partial:         (data.partial as number) ?? 0,
            fail:            (data.fail as number)   ?? 0,
            na:              (data.na as number)     ?? 0,
            complianceScore: (data.compliance_score as number) ?? 0,
            productName:     (data.product_name as string) || prev.productName,
            manufacturer:    (data.manufacturer as string) || prev.manufacturer,
            model:           (data.model as string) || prev.model,
            domains:         Array.isArray(data.domains) ? data.domains as string[] : prev.domains,
          }));
        },
        onError: (message) => {
          update({ phase: 'error', error: message });
        },
        onConfirmationNeeded: (guesses, summary) => {
          update({
            phase: 'confirmation_needed',
            guesses,
            productName: summary,
            confirmedProductType: guesses[0] ?? '',
          });
        },
      },
      confirmedType,
      confirmedDomains,
      ctrl.signal,
    );
  }, [update]);

  const handleSubmit = useCallback(() => {
    if (!selectedFile) return;
    void runReview(selectedFile);
  }, [selectedFile, runReview]);

  /* ── Confirmation step ────────────────────────────────────────── */

  const handleConfirmationSubmit = useCallback(() => {
    if (!selectedFile || !state.confirmedProductType.trim()) return;
    // Re-run with confirmed type and default domains
    void runReview(selectedFile, state.confirmedProductType.trim(), []);
  }, [selectedFile, state.confirmedProductType, runReview]);

  /* ── Filtered gaps ────────────────────────────────────────────── */

  const filteredGaps = useMemo(() => {
    return deferredGaps.filter(gap => {
      if (gradeFilter !== 'ALL' && gap.grade !== gradeFilter) return false;
      if (domainFilter !== 'ALL' && gap.domain !== domainFilter) return false;
      return true;
    });
  }, [deferredGaps, gradeFilter, domainFilter]);

  // Unique domains present in results
  const availableDomains = useMemo(() => {
    const seen = new Set<string>();
    for (const g of state.gaps) {
      if (g.domain) seen.add(g.domain);
    }
    return Array.from(seen);
  }, [state.gaps]);

  /* ── Export ───────────────────────────────────────────────────── */

  const handleCopyGap = useCallback((gap: ReviewGap, idx: number) => {
    const text = [
      `[${gap.grade}] ${gap.requirement}`,
      `Source: ${gap.source_standard}${gap.source_clause ? ` cl. ${gap.source_clause}` : ''}`,
      gap.manual_excerpt ? `Manual: ${gap.manual_excerpt}` : null,
      gap.recommendation ? `Recommendation: ${gap.recommendation}` : null,
    ].filter(Boolean).join('\n');
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  }, []);

  const handleCopyAll = useCallback(() => {
    const header = [
      `Manual Review Report — ${state.productName}`,
      state.manufacturer ? `Manufacturer: ${state.manufacturer}` : null,
      state.model ? `Model: ${state.model}` : null,
      `File: ${state.fileName}`,
      `Compliance Score: ${state.complianceScore}%`,
      `Results: ${state.pass} Pass / ${state.partial} Partial / ${state.fail} Fail / ${state.na} N/A`,
      `Domains: ${state.domains.map(d => DOMAIN_LABELS[d] || d).join(', ')}`,
      '───────────────────────────────────────',
    ].filter(Boolean).join('\n');

    const gaps = state.gaps.map((g, i) => [
      `${i + 1}. [${g.grade}] ${g.requirement}`,
      `   Source: ${g.source_standard}${g.source_clause ? ` cl. ${g.source_clause}` : ''}`,
      g.manual_excerpt ? `   Manual: ${g.manual_excerpt}` : null,
      g.recommendation ? `   ⚠ Fix: ${g.recommendation}` : null,
    ].filter(Boolean).join('\n')).join('\n\n');

    void navigator.clipboard.writeText(`${header}\n\n${gaps}`).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
    });
  }, [state]);

  const handleExportPDF = useCallback(() => {
    window.print();
  }, []);

  /* ── Render helpers ───────────────────────────────────────────── */

  const isProcessing = ['uploading', 'identifying', 'sweeping', 'grading'].includes(state.phase);

  // Compliance score ring SVG
  const scoreRing = useMemo(() => {
    const r = 34;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (state.complianceScore / 100) * circumference;
    const color = state.complianceScore >= 75 ? '#22c55e'
                : state.complianceScore >= 40 ? '#f59e0b'
                : '#ef4444';
    return { circumference, offset, color };
  }, [state.complianceScore]);

  /* ── Render ───────────────────────────────────────────────────── */

  return (
    <div className="manual-review-overlay" onClick={(e) => { if ((e.target as HTMLElement).classList.contains('manual-review-overlay')) handleClose(); }}>
      <div className="manual-review-modal" role="dialog" aria-modal="true" aria-label="Manual Review">

        {/* Header */}
        <div className="mr-header">
          <div className="mr-header-left">
            <span className="mr-icon">🔍</span>
            <div>
              <h2 className="mr-title">Manual Review</h2>
              <p className="mr-subtitle">Upload a product manual PDF to check compliance gaps</p>
            </div>
          </div>
          <button className="mr-close" onClick={handleClose} aria-label="Close">✕</button>
        </div>

        <div className="mr-body">

          {/* ── IDLE: Upload zone ── */}
          {state.phase === 'idle' && (
            <div className="mr-upload-section">
              <div
                className={`mr-dropzone${isDragging ? ' mr-dropzone--drag' : ''}${selectedFile ? ' mr-dropzone--selected' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                aria-label="Upload PDF"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="mr-file-input"
                  onChange={handleFileInput}
                  aria-hidden="true"
                />
                {selectedFile ? (
                  <>
                    <span className="mr-dropzone-icon">📄</span>
                    <p className="mr-dropzone-filename">{selectedFile.name}</p>
                    <p className="mr-dropzone-size">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                    <p className="mr-dropzone-hint">Click to change file</p>
                  </>
                ) : (
                  <>
                    <span className="mr-dropzone-icon">📂</span>
                    <p className="mr-dropzone-label">Drop a PDF here, or click to browse</p>
                    <p className="mr-dropzone-hint">Installation manuals, product guides — max 10MB</p>
                  </>
                )}
              </div>

              {selectedFile && (
                <button
                  className="mr-submit-btn"
                  onClick={handleSubmit}
                >
                  Review Manual →
                </button>
              )}
            </div>
          )}

          {/* ── PROCESSING: Progress indicator ── */}
          {isProcessing && (
            <div className="mr-processing">
              <div className="mr-processing-phases">
                {(['uploading', 'identifying', 'sweeping', 'grading'] as const).map((ph, i) => {
                  const phases: ManualReviewState['phase'][] = ['uploading', 'identifying', 'sweeping', 'grading'];
                  const currentIdx = phases.indexOf(state.phase as ManualReviewState['phase']);
                  const isDone = i < currentIdx;
                  const isActive = ph === state.phase;
                  return (
                    <div key={ph} className={`mr-phase-step${isDone ? ' mr-phase-step--done' : ''}${isActive ? ' mr-phase-step--active' : ''}`}>
                      <div className="mr-phase-indicator">
                        {isDone ? '✓' : isActive ? <span className="mr-spinner" /> : String(i + 1)}
                      </div>
                      <span className="mr-phase-label">
                        {ph === 'uploading'   ? 'Upload'
                       : ph === 'identifying' ? 'Identify Product'
                       : ph === 'sweeping'    ? 'Sweep Corpus'
                       : 'Grade Requirements'}
                      </span>
                    </div>
                  );
                })}
              </div>

              <p className="mr-status-msg">{PHASE_MESSAGES[state.phase]}</p>

              {state.phase === 'grading' && state.gaps.length > 0 && (
                <p className="mr-grading-count">{state.gaps.length} requirements graded…</p>
              )}

              {state.productName && (
                <p className="mr-product-found">
                  <strong>Product:</strong> {state.productName}
                  {state.manufacturer && <span> — {state.manufacturer}</span>}
                </p>
              )}
            </div>
          )}

          {/* ── CONFIRMATION NEEDED ── */}
          {state.phase === 'confirmation_needed' && (
            <div className="mr-confirmation">
              <div className="mr-confirm-icon">🤔</div>
              <h3 className="mr-confirm-title">Product not identified with confidence</h3>
              <p className="mr-confirm-desc">
                {state.productName || 'The product in this manual could not be identified clearly.'}
                <br />Please confirm the product type to proceed.
              </p>

              <div className="mr-confirm-guesses">
                {state.guesses.map((g) => (
                  <label key={g} className="mr-confirm-option">
                    <input
                      type="radio"
                      name="product-type-guess"
                      value={g}
                      checked={state.confirmedProductType === g}
                      onChange={() => update({ confirmedProductType: g })}
                    />
                    <span>{g}</span>
                  </label>
                ))}
                <label className="mr-confirm-option mr-confirm-option--other">
                  <input
                    type="radio"
                    name="product-type-guess"
                    value="__custom__"
                    checked={!state.guesses.includes(state.confirmedProductType)}
                    onChange={() => update({ confirmedProductType: '' })}
                  />
                  <span>Other:</span>
                  <input
                    type="text"
                    className="mr-confirm-custom"
                    placeholder="Enter product type…"
                    value={state.guesses.includes(state.confirmedProductType) ? '' : state.confirmedProductType}
                    onChange={(e) => update({ confirmedProductType: e.target.value })}
                  />
                </label>
              </div>

              <div className="mr-confirm-actions">
                <button className="mr-btn mr-btn--secondary" onClick={handleReset}>
                  Upload Different File
                </button>
                <button
                  className="mr-btn mr-btn--primary"
                  onClick={handleConfirmationSubmit}
                  disabled={!state.confirmedProductType.trim()}
                >
                  Confirm & Review →
                </button>
              </div>
            </div>
          )}

          {/* ── ERROR ── */}
          {state.phase === 'error' && (
            <div className="mr-error">
              <div className="mr-error-icon">⚠️</div>
              <p className="mr-error-msg">{state.error || 'An unexpected error occurred.'}</p>
              <div className="mr-error-actions">
                <button className="mr-btn mr-btn--secondary" onClick={handleReset}>
                  Try Again
                </button>
                <button className="mr-btn mr-btn--ghost" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          )}

          {/* ── COMPLETE: Results ── */}
          {state.phase === 'complete' && (
            <div className="mr-results">

              {/* Product header */}
              <div className="mr-results-header">
                <div className="mr-product-info">
                  <h3 className="mr-product-name">{state.productName}</h3>
                  {(state.manufacturer || state.model) && (
                    <p className="mr-product-meta">
                      {state.manufacturer && <span>{state.manufacturer}</span>}
                      {state.manufacturer && state.model && <span> · </span>}
                      {state.model && <span>Model: {state.model}</span>}
                    </p>
                  )}
                  <p className="mr-product-file">📄 {state.fileName}</p>
                  {state.domains.length > 0 && (
                    <div className="mr-domain-chips">
                      {state.domains.map(d => (
                        <span key={d} className="mr-domain-chip">{DOMAIN_LABELS[d] || d}</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Compliance score ring */}
                <div className="mr-score-ring-wrap">
                  <svg className="mr-score-ring" viewBox="0 0 80 80" width="80" height="80">
                    <circle cx="40" cy="40" r="34" fill="none" stroke="var(--otto-border, #e2e8f0)" strokeWidth="8" />
                    <circle
                      cx="40" cy="40" r="34" fill="none"
                      stroke={scoreRing.color}
                      strokeWidth="8"
                      strokeDasharray={scoreRing.circumference}
                      strokeDashoffset={scoreRing.offset}
                      strokeLinecap="round"
                      transform="rotate(-90 40 40)"
                      style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                    />
                    <text x="40" y="43" textAnchor="middle" fontSize="13" fontWeight="700" fill="currentColor">
                      {state.complianceScore}%
                    </text>
                  </svg>
                  <p className="mr-score-label">Compliant</p>
                </div>
              </div>

              {/* Summary bar */}
              <div className="mr-summary-bar">
                {ALL_GRADES.map(g => {
                  const count = g === 'PASS' ? state.pass
                              : g === 'PARTIAL' ? state.partial
                              : g === 'FAIL' ? state.fail
                              : state.na;
                  return (
                    <button
                      key={g}
                      className={`mr-summary-chip${gradeFilter === g ? ' mr-summary-chip--active' : ''}`}
                      style={{ '--chip-color': REVIEW_GRADE_COLORS[g] } as React.CSSProperties}
                      onClick={() => setGradeFilter(prev => prev === g ? 'ALL' : g)}
                      title={`Filter by ${REVIEW_GRADE_LABELS[g]}`}
                    >
                      <span className="mr-chip-dot" style={{ background: REVIEW_GRADE_COLORS[g] }} />
                      <span className="mr-chip-count">{count}</span>
                      <span className="mr-chip-label">{REVIEW_GRADE_LABELS[g]}</span>
                    </button>
                  );
                })}
                {gradeFilter !== 'ALL' && (
                  <button className="mr-clear-filter" onClick={() => setGradeFilter('ALL')}>
                    Clear filter ✕
                  </button>
                )}
              </div>

              {/* Filter row */}
              <div className="mr-filters">
                <label className="mr-filter-label">
                  Domain:
                  <select
                    className="mr-filter-select"
                    value={domainFilter}
                    onChange={e => setDomainFilter(e.target.value)}
                  >
                    <option value="ALL">All domains</option>
                    {availableDomains.map(d => (
                      <option key={d} value={d}>{DOMAIN_LABELS[d] || d}</option>
                    ))}
                  </select>
                </label>

                <div className="mr-filter-spacer" />

                <div className="mr-export-buttons">
                  <button className="mr-btn mr-btn--sm" onClick={handleCopyAll} title="Copy full report to clipboard">
                    {copiedAll ? '✓ Copied' : '📋 Copy Report'}
                  </button>
                  <button className="mr-btn mr-btn--sm" onClick={handleExportPDF} title="Export as PDF">
                    📄 Export PDF
                  </button>
                </div>
              </div>

              {/* Gap list */}
              <div className="mr-gap-list">
                {filteredGaps.length === 0 ? (
                  <p className="mr-no-results">No requirements match the selected filters.</p>
                ) : (
                  filteredGaps.map((gap, idx) => (
                    <GapCard
                      key={`${gap.source_standard}-${gap.source_clause}-${idx}`}
                      gap={gap}
                      idx={idx}
                      copied={copiedIdx === idx}
                      onCopy={() => handleCopyGap(gap, idx)}
                    />
                  ))
                )}
              </div>

              {/* Footer actions */}
              <div className="mr-results-footer">
                <button className="mr-btn mr-btn--secondary" onClick={handleReset}>
                  Review Another Manual
                </button>
                <button className="mr-btn mr-btn--ghost" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── GapCard ────────────────────────────────────────────────────────

interface GapCardProps {
  gap: ReviewGap;
  idx: number;
  copied: boolean;
  onCopy: () => void;
}

function GapCard({ gap, idx, copied, onCopy }: GapCardProps) {
  const [expanded, setExpanded] = useState(false);
  const color = REVIEW_GRADE_COLORS[gap.grade];
  const label = REVIEW_GRADE_LABELS[gap.grade];

  return (
    <div className={`mr-gap-card mr-gap-card--${gap.grade.toLowerCase()}`}>
      <div className="mr-gap-card-header" onClick={() => setExpanded(x => !x)} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter') setExpanded(x => !x); }}>
        <span
          className="mr-grade-badge"
          style={{ background: color + '22', color, border: `1px solid ${color}55` }}
        >
          {label}
        </span>
        <span className="mr-gap-requirement">{gap.requirement}</span>
        <div className="mr-gap-card-right">
          {gap.source_standard && (
            <span className="mr-gap-citation">
              {gap.source_standard}
              {gap.source_clause && ` cl. ${gap.source_clause}`}
            </span>
          )}
          <span className="mr-gap-expand">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="mr-gap-card-body">
          {gap.manual_excerpt && (
            <div className="mr-gap-excerpt">
              <span className="mr-gap-excerpt-label">Found in manual:</span>
              <blockquote className="mr-gap-quote">"{gap.manual_excerpt}"</blockquote>
            </div>
          )}
          {gap.recommendation && (
            <div className="mr-gap-recommendation">
              <span className="mr-gap-rec-label">Recommendation:</span>
              <p className="mr-gap-rec-text">{gap.recommendation}</p>
            </div>
          )}
          {!gap.manual_excerpt && !gap.recommendation && (
            <p className="mr-gap-na-note">
              {gap.grade === 'NA'
                ? 'This requirement does not apply to the identified product.'
                : 'No specific excerpt or recommendation for this item.'}
            </p>
          )}
          <button className="mr-copy-gap" onClick={onCopy} title="Copy this item">
            {copied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      )}
    </div>
  );
}
